import { useNavigate, useParams } from "react-router-dom";
import { API } from "../../../config";
import { useEffect, useState } from "react";
import { removeImageBackground } from "../../../_helpers/image";

const calculateDiscountedCost = (mrpValue, discountValue) => {
  const mrp = Number(mrpValue) || 0;
  const margin = Number(discountValue) || 0; // "discount" field now represents margin
  const costWithMargin = mrp * (1 + margin / 100);
  return Number.isFinite(costWithMargin)
    ? Number(costWithMargin.toFixed(2))
    : 0;
};

const calculateDiscountFromCost = (mrpValue, costValue) => {
  const mrp = Number(mrpValue) || 0;
  const cost = Number(costValue) || 0;
  if (mrp === 0) {
    return 0;
  }
  // Inverse of costWithMargin = mrp * (1 + margin/100)
  const margin = (cost / mrp - 1) * 100;
  return Number.isFinite(margin) ? Number(margin.toFixed(2)) : 0;
};

const isLocalImage = (imageUrl) => {
  if (!imageUrl || typeof imageUrl !== "string") return false;
  return imageUrl.startsWith("blob:") || imageUrl.startsWith("data:");
};

const isBase64Payload = (value) => {
  if (!value || typeof value !== "string") return false;
  if (
    value.startsWith("http://") ||
    value.startsWith("https://") ||
    value.startsWith("blob:") ||
    value.startsWith("data:")
  ) {
    return false;
  }
  const sanitized = value.replace(/\s/g, "");
  return /^[A-Za-z0-9+/=]+$/.test(sanitized);
};

const shouldUploadImage = (image) => {
  if (!image) return false;
  if (typeof image === "string") {
    return isLocalImage(image) || isBase64Payload(image);
  }
  return image instanceof File || image instanceof Blob;
};

const base64StringToFile = (base64String, fileName) => {
  const matches = /^data:(.+);base64,/.exec(base64String);
  const mimeType = matches ? matches[1] : "image/png";
  const payload = matches ? base64String.split(",")[1] : base64String;
  const binary = atob(payload);
  const len = binary.length;
  const bytes = new Uint8Array(len);
  for (let i = 0; i < len; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  return new File([bytes], fileName, { type: mimeType });
};

export const createFileFromSource = async (image, fileName) => {
  if (typeof image === "string") {
    if (isLocalImage(image)) {
      const response = await fetch(image);
      const blob = await response.blob();
      return new File([blob], fileName, {
        type: blob.type || "image/png",
      });
    }

    if (isBase64Payload(image)) {
      return base64StringToFile(image, fileName);
    }
  }

  if (image instanceof File) {
    return image;
  }

  if (image instanceof Blob) {
    return new File([image], fileName, {
      type: image.type || "image/png",
    });
  }

  return null;
};

export const useProduct = () => {
  const [removeBgLoading, setRemoveBgLoading] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState(null);
  const handleSelectImage = (index) => {
    setSelectedImageIndex(index);
  };

  const getCatalogDetails = async () => {
    const response = await fetch(`${API}/catalogs/${catalogId}`);
    const json = await response.json();
    setProduct((prev) => ({
      ...prev,
      discount: json.discount,
    }));
    return json;
  };

  const navigate = useNavigate();

  const uploadImage = async (image) => {
    const formData = new FormData();
    formData.append("file", image);
    const response = await fetch(`${API}/image-upload`, {
      method: "POST",
      body: formData,
    });
    const json = await response.json();
    return json;
  };

  const uploadImageIfNeeded = async (image, fileName) => {
    if (!shouldUploadImage(image)) {
      return image;
    }

    const file = await createFileFromSource(image, fileName);
    if (!file) {
      return image;
    }

    const result = await uploadImage(file);
    return result.result;
  };

  const handleRemoveImage = (index) => {
    setProduct((prev) => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index),
    }));
  };

  const [loading, setLoading] = useState(false);
  const { id, catalogId } = useParams();
  const [product, setProduct] = useState({
    name: "",
    brandName: "",
    categoryId: "",
    type: "",
    sortId: "",
    availability: true,
    customization: false,
    images: [],
    description: "",
    itemDetails: [],
    mrp: 0,
    discount: 0,
    cost: 0,
    variants: [],
  });
  const getProductDetails = async () => {
    setLoading(true);
    const res = await fetch(
      `${API}/catalogs/${catalogId}/products/${id}`
    ).then((res) => res.json());
    if (!res.product) {
      navigate(`/catalogs/${catalogId}`);
      return;
    }
    setProduct((prev) => {
      const loaded = res.product;
      const mrp = Number(loaded.mrp) || 0;
      const discount = Number(loaded.discount) || 0; // "discount" now represents margin%
      const cost = calculateDiscountedCost(mrp, discount);

      return {
        ...prev,
        ...loaded,
        mrp,
        discount,
        cost,
      };
    });
    setLoading(false);
  };

  useEffect(() => {
    if (!id) {
      if (!catalogId) return;
      getCatalogDetails();
    } else {
      getProductDetails();
    }
  }, [id]);

  const updateProduct = async (productData) => {
    const response = await fetch(
      `${API}/catalogs/${catalogId}/products/${id}`,
      {
        method: "PUT",
        body: JSON.stringify(productData),
      }
    );
    const json = await response.json();
    return json;
  };

  const createProduct = async (productData) => {
    const response = await fetch(`${API}/catalogs/${catalogId}/products`, {
      method: "POST",
      body: JSON.stringify(productData),
    });
    const json = await response.json();
    return json;
  };

  const handleSubmit = async (productData) => {
    try {
      if (productData.images && Array.isArray(productData.images)) {
        await Promise.all(
          productData.images.map(async (image, index) => {
            if (!image) return;
            productData.images[index] = await uploadImageIfNeeded(
              image,
              `image_${index}.png`
            );
          })
        );
      }

      if (Array.isArray(productData.variants)) {
        await Promise.all(
          productData.variants.map(async (variant, variantIndex) => {
            if (!variant || !Array.isArray(variant.images)) {
              return;
            }

            await Promise.all(
              variant.images.map(async (image, imageIndex) => {
                if (!image) return;

                const uploaded = await uploadImageIfNeeded(
                  image,
                  `variant_${variantIndex}_image_${imageIndex}.png`
                );

                if (!Array.isArray(variant.images)) {
                  variant.images = [];
                }
                variant.images[imageIndex] = uploaded;
              })
            );
          })
        );
      }

      console.log(productData);
      if (id) {
        await updateProduct(productData);
      } else {
        await createProduct(productData);
      }
      navigate(`/catalogs/${catalogId}`);
    } catch (error) {
      console.log(error);
    }
  };

  const handleDelete = async () => {
    await fetch(`${API}/catalogs/${catalogId}/products/${id}`, {
      method: "DELETE",
    });
    navigate(`/catalogs/${catalogId}`);
    getCatalogDetails();
  };

  const handleRemoveBg = async (imageIndex) => {
    setRemoveBgLoading(true);
    const result = await removeImageBackground(product.images[imageIndex]);
    if (result) {
      const imageUrl = URL.createObjectURL(result);
      setProduct((prev) => ({
        ...prev,
        images: prev.images.map((image, index) =>
          index === imageIndex ? imageUrl : image
        ),
      }));
    }
    setRemoveBgLoading(false);
  };

  const handleChange = (key, value) => {
    if (!key) return;

    setProduct((prev) => {
      if (key === "mrp" || key === "discount" || key === "cost") {
        const numericValue = Number(value) || 0;
        const currentMrp = Number(prev.mrp) || 0;
        const currentDiscount = Number(prev.discount) || 0;

        if (key === "cost") {
          const updatedDiscount = calculateDiscountFromCost(
            currentMrp,
            numericValue
          );

          return {
            ...prev,
            cost: numericValue,
            discount: updatedDiscount,
          };
        }

        const nextMrp = key === "mrp" ? numericValue : currentMrp;
        const nextDiscount =
          key === "discount" ? numericValue : currentDiscount;

        return {
          ...prev,
          mrp: nextMrp,
          discount: nextDiscount,
          cost: calculateDiscountedCost(nextMrp, nextDiscount),
        };
      }

      if (key === "variants") {
        return {
          ...prev,
          variants: Array.isArray(value) ? value : prev.variants,
        };
      }

      if (key === "itemDetails") {
        return {
          ...prev,
          itemDetails: Array.isArray(value) ? value : prev.itemDetails,
        };
      }

      if (key.startsWith("variants.")) {
        const [, indexString, field] = key.split(".");
        const index = Number(indexString);
        if (Number.isNaN(index) || !field) {
          return prev;
        }

        const updatedVariants = [...(prev.variants || [])];
        const existingVariant = updatedVariants[index] || {};
        updatedVariants[index] = {
          ...existingVariant,
          [field]: value,
        };

        return {
          ...prev,
          variants: updatedVariants,
        };
      }

      if (key.startsWith("itemDetails.")) {
        const [, indexString] = key.split(".");
        const index = Number(indexString);
        if (Number.isNaN(index)) {
          return prev;
        }

        const updatedItemDetails = [...(prev.itemDetails || [])];
        updatedItemDetails[index] = value;

        return {
          ...prev,
          itemDetails: updatedItemDetails,
        };
      }

      return {
        ...prev,
        [key]: value,
      };
    });
  };

  return {
    product,
    handleChange,
    handleSubmit,
    productDetailsLoading: loading,
    handleDelete,
    handleRemoveBg,
    handleSelectImage,
    selectedImageIndex,
    setProduct,
    removeBgLoading,
    handleRemoveImage,
  };
};
